import { Product } from "@/api/product-api";
import { useTrans } from "@/utils/useTrans";
import React, { useState } from "react";
import BleAudioIcon from "@/assets/icons/BleAudioIcon";
import BleConnectivityIcon from "@/assets/icons/BleConnectivityIcon";
import FitAllIcon from "@/assets/icons/FitAllIcon";
import {
  getCompatibility,
  getTechSpec,
  TECH_SPEC_SLUG_LIST,
  TechSpec,
} from "@/libs/tactsuit/getTechSpecs";
import { ProductSelect } from "@/components/tactsuit/product-comparison/product-select";
import { cn } from "@/libs/utils";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { produce } from "immer";

type DivProps = React.HTMLAttributes<HTMLDivElement>;

// 제품의 호환성 정보를 변환
const parseCompatibilities = (
  products: string[],
): { title: string; desc: string }[][] => {
  const list: { title: string; desc: string }[][] = products.map((p) => {
    const data = getCompatibility(p);
    return data.map((item) => ({
      title: item.platform,
      desc: item.value,
    }));
  });

  // 제품의 호환성 수가 다른제품과 다른 경우 비어있는 부분 추가
  const max = Math.max(...list.map((v) => v.length));
  const filled = list.map((item) =>
    Array.from({ length: max }, (_, i) => item[i] ?? null),
  );

  return Array.from({ length: products.length }, (_, i) =>
    filled.filter((_, index) => index % products.length === i).flat(),
  );
};

const parser = {
  hapticPoints: (specs: TechSpec[]) =>
    specs.map(({ hapticPoints }) => ({
      title: hapticPoints.number + "",
      desc: hapticPoints.description,
      sub: "ERM Motors",
    })),
  connectivity: (specs: TechSpec[]) =>
    specs.map(({ connectivity }) => ({
      icon:
        connectivity.type === "Dual" ? (
          <BleAudioIcon />
        ) : (
          <BleConnectivityIcon />
        ),
      desc: connectivity.type === "Dual" ? "BLE & audio port¹" : "BLE",
    })),
  playtime: (specs: TechSpec[]) =>
    specs.map(({ playtime, slug }) => ({
      sup: "Up to",
      title: `${playtime.hours} Hrs`,
      desc:
        slug?.includes("tactsuit") || slug?.includes("tactvisor")
          ? "Playtime²"
          : "Playtime³",
    })),
  sizeDetails: (specs: TechSpec[]) =>
    specs.map(({ sizeDetails, slug }) => ({
      icon: <FitAllIcon />,
      desc: sizeDetails.type,
    })),
  weight: (specs: TechSpec[]) =>
    specs.map(({ sizeDetails, slug }) => ({
      subtitle: "Weight",
      desc: sizeDetails.weight || "-",
    })),
  width: (specs: TechSpec[]) =>
    specs.map(({ sizeDetails, slug }) => ({
      subtitle: "Width",
      desc: sizeDetails.weight || sizeDetails.size || "-",
    })),
  battery: (specs: TechSpec[]) =>
    specs.map(({ chargingAndBattery, slug }) => ({
      desc: chargingAndBattery.batterySpec,
    })),
  length: (specs: TechSpec[]) =>
    specs.map(({ sizeDetails, slug }) => ({
      subtitle: "Length",
      closedLength: sizeDetails.closedLength,
      openedLength: sizeDetails.openedLength,
    })),
};

const Flex = ({ className, ...rest }: DivProps) => (
  <div
    className={cn(
      "flex flex-col items-center gap-x-1 gap-y-2 data-[index=2]:hidden laptop:data-[index=2]:flex",
      className,
    )}
    {...rest}
  />
);

const LengthCard = ({
  closedLength,
  openedLength,
}: DivProps & {
  closedLength?: string;
  openedLength?: string;
}) => {
  const { t } = useTrans(["tactsuit"]);
  const lengthNotDefined = !closedLength && !openedLength;
  return (
    <div>
      {lengthNotDefined && <h6>-</h6>}
      {!!closedLength && (
        <div>
          {t("Closed length")}: {closedLength}
        </div>
      )}
      {!!openedLength && (
        <div>
          {t("Opened length")}: {openedLength}
        </div>
      )}
    </div>
  );
};

const BaseCard = ({
  sup,
  sub,
  icon,
  title,
  desc,
  subtitle,
  className,
  children,
  ...rest
}: DivProps & {
  sup?: string;
  sub?: string;
  icon?: React.ReactElement;
  title?: string;
  subtitle?: string;
  desc?: string;
  category?: string | "length";
}) => {
  const { t } = useTrans(["tactsuit"]);
  return (
    <Flex
      className={cn("text-center text-base font-400 laptop:text-lg", className)}
      {...rest}
    >
      {!!sup && <span>{sup}</span>}
      {!!icon && icon}
      {!!title && (
        <h3 className="text-[36px] leading-[44px] laptop:text-[45px] laptop:leading-[52px]">
          {t(title)}
        </h3>
      )}
      {!!subtitle && <h3 className="font-600">{t(subtitle)}</h3>}
      {!!desc && <h6 className="grow">{desc}</h6>}
      {children}
      {!!sub && <p className="text-base text-coolgray-60">{t(sub)}</p>}
    </Flex>
  );
};

const CompatibilityCard = ({
  title,
  desc,
  className,
  ...rest
}: DivProps & {
  title?: string;
  desc?: string;
}) => {
  const { t } = useTrans(["tactsuit"]);
  return (
    <Flex
      className={cn("text-center text-base font-400 laptop:text-lg", className)}
      {...rest}
    >
      {!!title && (
        <h3 className="min-h-9 font-600 laptop:min-h-0">{t(title)}</h3>
      )}
      {!!desc && <h6 className="grow">{t(desc)}</h6>}
    </Flex>
  );
};

const Section = ({ className, ...rest }: DivProps) => (
  <section
    {...rest}
    className={cn("mt-9 space-y-[100px] laptop:mt-[50px]", className)}
  />
);
const Grid = ({ className, ...rest }: DivProps) => (
  <div
    {...rest}
    className={cn("grid auto-cols-fr grid-flow-col gap-x-1", className)}
  />
);

function Heading({ className, ...rest }: DivProps) {
  return (
    <h3
      className={cn(
        "border-b border-coolgray-20 py-5",
        "text-start text-base font-600 laptop:text-lg",
        className,
      )}
      {...rest}
    />
  );
}

const CompatibilitiesSection = ({
  compatibilities,
}: {
  compatibilities: {
    title: string;
    desc: string;
  }[][];
}) => (
  <Section>
    {[...Array(compatibilities[0].length)].map((_, index) => {
      const columns = compatibilities.map((_, i) => compatibilities[i][index]);
      return (
        <Grid key={index}>
          {columns.map((column, i) => (
            <CompatibilityCard key={i} {...column} data-index={i} />
          ))}
        </Grid>
      );
    })}
  </Section>
);

function ComparisonWithSpecs({
  products,
  slug,
  max = 3,
}: {
  products: Product[];
  slug: string;
  max?: number;
}) {
  const sorted = TECH_SPEC_SLUG_LIST.sort((a, b) => (a === slug ? -1 : 1));
  const [slugs, setSlugs] = useState(sorted.slice(0, max));

  const sortedProducts: Product[] = sorted
    .map((slug) => products.find((p) => p.slug === slug))
    .filter((p) => !!p);

  const selectedProducts: Product[] = slugs
    .map((slug) => products.find((p) => p.slug === slug))
    .filter((p) => !!p);

  const handleChangeSlug = (index: number, value: string) => {
    setSlugs(
      produce((draft) => {
        const item = TECH_SPEC_SLUG_LIST.find((p) => p === value);
        if (item) draft[index] = item;
      }),
    );
  };

  return (
    <div className="mx-auto my-7 laptop:my-16">
      <CompareProductsHead
        products={sortedProducts}
        selectedProducts={selectedProducts}
        onChangeSlug={handleChangeSlug}
      />
      <CompareProductsBody slugs={slugs} />
    </div>
  );
}

export function CompareProductsHead({
  products,
  className,
  onChangeSlug,
  selectedProducts,
}: {
  selectedProducts: Product[];
  className?: string;
  products: Product[];
  onChangeSlug?: (index: number, slug: string) => void;
}) {
  const { t } = useTrans(["tactsuit"]);
  const isMobileView = useIsMobileView();

  const handleProductChange = (index: number, value: string) => {
    onChangeSlug?.(index, value);
  };

  return (
    <div className={cn(className)}>
      <h1 className="mb-8 text-3xl font-700">{t("Compare")}</h1>
      <div className="grid grid-cols-2 gap-x-4 laptop:grid-cols-3 laptop:gap-x-7">
        {selectedProducts
          .filter((_, index) => (isMobileView ? index < 2 : true))
          .map((p, index) => (
            <ProductSelect
              key={`${p.slug}-${index}`}
              products={products}
              selected={p}
              index={index}
              onValueChange={handleProductChange}
            />
          ))}
      </div>
    </div>
  );
}

export function CompareProductsBody({
  className,
  slugs = [],
}: {
  className?: string;
  slugs: string[];
}) {
  const { t } = useTrans(["tactsuit"]);

  const techSpecs = slugs.map((p) => getTechSpec(p));
  const hapticPoints = parser.hapticPoints(techSpecs);
  const connectivity = parser.connectivity(techSpecs);
  const playtime = parser.playtime(techSpecs);
  const sizeDetails = parser.sizeDetails(techSpecs);
  const width = parser.width(techSpecs);
  const length = parser.length(techSpecs);
  const weight = parser.weight(techSpecs);
  const battery = parser.battery(techSpecs);
  const compatibilities = parseCompatibilities(slugs);

  return (
    <div className={cn(className)}>
      <Heading>{t("Quick Look")}</Heading>
      <Section>
        <Grid>
          {hapticPoints.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
        <Grid>
          {connectivity.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
        <Grid>
          {playtime.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
        <Grid>
          {sizeDetails.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
      </Section>
      <div className="col-span-2 mt-14 text-sm text-coolgray-50 laptop:col-span-3 laptop:mt-18">
        {[
          `1. Wired connection supports stereo(2ch) haptic feedback only.`,
          `2. When all feedback points are operating for 1 sec every 10sec at maximum intensity.`,
          `3. When all feedback points are operating for 1 sec. in every 5 sec. at maximum intensity.`,
        ].map((text, index) => (
          <p key={index}>{t(text)}</p>
        ))}
      </div>
      <Heading className="mt-14 laptop:mt-18">{t("Size and Weight")}</Heading>
      <Section>
        <Grid>
          {width.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
        <Grid>
          {length.map((item, index) => {
            return (
              <BaseCard key={index} data-index={index}>
                <LengthCard {...item} />
              </BaseCard>
            );
          })}
        </Grid>
        <Grid>
          {weight.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
      </Section>
      <Heading className="mt-14 laptop:mt-18">{t("Battery")}</Heading>
      <Section>
        <Grid>
          {battery.map((item, index) => (
            <BaseCard key={index} {...item} data-index={index} />
          ))}
        </Grid>
      </Section>
      <Heading className="mt-14 laptop:mt-18">{t("Compatibility")}</Heading>
      <CompatibilitiesSection compatibilities={compatibilities} />
    </div>
  );
}

export default ComparisonWithSpecs;
